import AddRoundedIcon from '@mui/icons-material/AddRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import { Alert, Box, Button, Chip, IconButton, LinearProgress, Stack, Typography } from '@mui/material';
import React from 'react';
import { useHistory } from 'react-router-dom';
import { IVerbaScholaCourseSummary, VerbaScholaApi } from '../api/VerbaScholaApi';
import VerbaScholaTeacherShell, { VerbaScholaCard, VerbaScholaCardHeader, VerbaScholaEmptyState } from '../component/VerbaScholaTeacherShell';
import useVerbaScholaResource from '../hook/useVerbaScholaResource';
import { BLUE, BLUE_HOVER, BORDER, INTER, NAVY, POPPINS, PROGRESS_TRACK, SLATE, SOFT_BLUE, SURFACE_TINT, WARNING_SOFT, WARNING_TEXT } from '../style/verbaScholaStyles';

const WEEKDAY_OPTIONS = [
    { value: 'monday', shortLabel: 'Mon' },
    { value: 'tuesday', shortLabel: 'Tue' },
    { value: 'wednesday', shortLabel: 'Wed' },
    { value: 'thursday', shortLabel: 'Thu' },
    { value: 'friday', shortLabel: 'Fri' },
    { value: 'saturday', shortLabel: 'Sat' },
    { value: 'sunday', shortLabel: 'Sun' },
];

function percentLabel(value: number | null | undefined, nullLabel = '0%'): string {
    if (value === null) return nullLabel;
    return typeof value === 'number' && Number.isFinite(value) ? `${Math.round(value)}%` : '0%';
}

function teacherGreeting(profile: { first_name?: string | null } | null): string {
    const firstName = profile?.first_name?.trim();
    return firstName ? `Good morning, ${firstName}` : 'Good morning';
}

function courseMeta(item: IVerbaScholaCourseSummary): string {
    const studentCount = item.summary.student_count;
    const courseDetails = [item.course.semester, meetingDaysLabel(item.course.meeting_days), startTimeLabel(item.course.start_time), item.course.room].filter(Boolean).join(' \u00b7 ');
    const studentLabel = `${studentCount} ${studentCount === 1 ? 'student' : 'students'}`;
    return courseDetails ? `${studentLabel} \u00b7 ${courseDetails}` : studentLabel;
}

function meetingDaysLabel(meetingDays: string[] | null | undefined): string | null {
    const selected = (Array.isArray(meetingDays) ? meetingDays : [])
        .map(day => WEEKDAY_OPTIONS.find(option => option.value === day)?.shortLabel)
        .filter(Boolean);
    return selected.length ? selected.join(' / ') : null;
}

function startTimeLabel(startTime: string | null | undefined): string | null {
    const match = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(startTime ?? '');
    if (!match) return null;
    const hour24 = Number(match[1]);
    const hour12 = hour24 % 12 || 12;
    const period = hour24 >= 12 ? 'PM' : 'AM';
    return `${hour12}:${match[2]} ${period}`;
}

function clampProgress(value: number | null | undefined): number {
    return typeof value === 'number' && Number.isFinite(value) ? Math.max(0, Math.min(100, value)) : 0;
}

export default function VerbaScholaTeacherDashboardPage() {
    const history = useHistory();
    const { data, loading, error, reload } = useVerbaScholaResource(() => VerbaScholaApi.getDashboard(), []);
    const [creating, setCreating] = React.useState(false);
    const [createError, setCreateError] = React.useState<string | null>(null);

    const courseList: IVerbaScholaCourseSummary[] = Array.isArray(data?.courses) ? data.courses : [];
    const dataShapeError = data?.courses != null && !Array.isArray(data.courses) ? 'Could not load classes.' : null;
    const activeCourseCount = courseList.filter(item => !item.course.is_archived).length;
    const studentTotal = courseList.reduce((total, item) => total + item.summary.student_count, 0);

    const createCourse = () => {
        setCreating(true);
        setCreateError(null);
        VerbaScholaApi.createCourse({ name: 'Untitled New Class', is_archived: false })
            .then(response => history.push(`/teacher/course/${response.data.id}/settings`))
            .catch(() => setCreateError('Could not create the class. Please try again.'))
            .finally(() => setCreating(false));
    };

    return (
        <VerbaScholaTeacherShell
            title={teacherGreeting}
            loading={loading}
            error={dataShapeError ?? error}
            onRetry={reload}
        >
            <Box sx={{ maxWidth: 900 }}>
                {createError && (
                    <Alert severity="error" sx={{ fontFamily: INTER, mb: 2 }} onClose={() => setCreateError(null)}>
                        {createError}
                    </Alert>
                )}

                <VerbaScholaCard>
                    <VerbaScholaCardHeader
                        title="Your classes"
                        meta={`${activeCourseCount} active ${activeCourseCount === 1 ? 'class' : 'classes'} \u00b7 ${studentTotal} ${studentTotal === 1 ? 'student' : 'students'}`}
                        actions={
                            <Button
                                startIcon={<AddRoundedIcon />}
                                disabled={creating}
                                onClick={createCourse}
                                sx={{ fontFamily: INTER, fontWeight: 700, fontSize: 12.5, color: BLUE, textTransform: 'none' }}
                            >
                                {creating ? 'Creating...' : 'Create class'}
                            </Button>
                        }
                    />

                    {courseList.length === 0 ? (
                        <VerbaScholaEmptyState message="You do not have any classes yet." />
                    ) : (
                        <Stack divider={<Box sx={{ height: '1px', backgroundColor: BORDER }} />}>
                            {courseList.map((item, index) => {
                                const color = index % 2 ? NAVY : BLUE;
                                const currentLessonTitle = item.summary.current_lesson_title?.trim();
                                const completionPercent = clampProgress(item.summary.current_lesson_completion_percent);
                                return (
                                    <Box
                                        key={item.course.id}
                                        onClick={() => history.push(`/teacher/course/${item.course.id}/overview`)}
                                        sx={{ p: { xs: 2, sm: 2.75 }, cursor: 'pointer', '&:hover': { backgroundColor: SURFACE_TINT } }}
                                    >
                                        <Stack direction="row" alignItems="flex-start" spacing={1.5}>
                                            <Box sx={{ width: 46, height: 46, flexShrink: 0, borderRadius: '14px', display: 'grid', placeItems: 'center', backgroundColor: `${color}12`, color }}>
                                                <GroupsRoundedIcon />
                                            </Box>
                                            <Box sx={{ minWidth: 0, flex: 1 }}>
                                                <Stack direction="row" alignItems="center" spacing={1}>
                                                    <Typography sx={{ fontFamily: POPPINS, fontWeight: 600, fontSize: 14.5, color: NAVY }}>
                                                        {item.course.name}
                                                    </Typography>
                                                    {item.course.is_archived && (
                                                        <Chip
                                                            label="Archived"
                                                            size="small"
                                                            sx={{ fontFamily: INTER, fontWeight: 600, fontSize: 11, color: WARNING_TEXT, backgroundColor: WARNING_SOFT }}
                                                        />
                                                    )}
                                                </Stack>
                                                <Typography sx={{ fontFamily: INTER, fontSize: 11.5, color: SLATE, mt: 0.25 }}>{courseMeta(item)}</Typography>
                                            </Box>
                                            <IconButton
                                                aria-label={`Open ${item.course.name}`}
                                                onClick={event => {
                                                    event.stopPropagation();
                                                    history.push(`/teacher/course/${item.course.id}/overview`);
                                                }}
                                                sx={{ mt: -0.5, mr: -0.5, color: BLUE, backgroundColor: SOFT_BLUE, '&:hover': { backgroundColor: BLUE_HOVER } }}
                                            >
                                                <ChevronRightRoundedIcon />
                                            </IconButton>
                                        </Stack>

                                        <Box sx={{ ml: { xs: 0, sm: 7.75 }, mt: 2 }}>
                                            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={0.75}>
                                                <Box>
                                                    <Typography sx={{ fontFamily: INTER, fontWeight: 600, fontSize: 11.5, color: SLATE, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Current lesson</Typography>
                                                    <Typography sx={{ fontFamily: INTER, fontWeight: 700, fontSize: 12.5, color: NAVY, mt: 0.3 }}>
                                                        {currentLessonTitle || 'No current lesson yet'}
                                                    </Typography>
                                                </Box>
                                                <Typography sx={{ alignSelf: { sm: 'flex-end' }, fontFamily: INTER, fontWeight: 700, fontSize: 11.5, color: NAVY }}>{percentLabel(completionPercent)} complete</Typography>
                                            </Stack>
                                            <LinearProgress
                                                aria-label={`${item.course.name} completion progress`}
                                                variant="determinate"
                                                value={completionPercent}
                                                sx={{ mt: 1, height: 6, borderRadius: 3, backgroundColor: PROGRESS_TRACK, '& .MuiLinearProgress-bar': { borderRadius: 3, backgroundColor: color } }}
                                            />
                                        </Box>
                                    </Box>
                                );
                            })}
                        </Stack>
                    )}
                </VerbaScholaCard>
            </Box>
        </VerbaScholaTeacherShell>
    );
}
